import { ApiResponse } from '@/domain';
import { useToast } from '@/hooks/use-toast';
import { agreementKeys } from './useAgreements';
import { CreatePaymentPlanRequest, paymentPlansApi } from '@/rest/modules/payment-plans-api';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { SubscriptionFilters } from '@/domain/agreements/agreement';

export const paymentPlanKeys = {
  all: ['paymentPlans'] as const,
  lists: () => [...paymentPlanKeys.all, 'list'] as const,
  list: (filters: SubscriptionFilters) => [...paymentPlanKeys.lists(), { filters }] as const,
  detail: (id: string) => [...paymentPlanKeys.all, 'detail', id] as const,
  stats: () => [...paymentPlanKeys.all, 'stats'] as const,
}

export const usePaymentPlans = (filters?: SubscriptionFilters) => {
  return useQuery({
    queryKey: paymentPlanKeys.list(filters || {}),
    queryFn: () => paymentPlansApi.getPaymentPlans(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

export const useCreatePaymentPlan = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreatePaymentPlanRequest) =>
      paymentPlansApi.createPaymentPlan(data),
    onSuccess: () => {
      toast({
        title: 'Éxito',
        description: 'Plan de pagos creado correctamente',
      });
      window.location.reload();
    },
    onError: (response: any) => {
      toast({
        title: 'Error',
        description: response?.message || response?.error || 'Error al crear el plan de pagos',
        variant: 'destructive',
      });
    },
  });
};


export const useDeletePaymentPlan = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => paymentPlansApi.deletePaymentPlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentPlanKeys.lists() })
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() })
      queryClient.invalidateQueries({ queryKey: agreementKeys.stats() })
      toast({
        title: 'Éxito',
        description: 'Plan de pago eliminado correctamente',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al eliminar el plan de pago',
        variant: 'destructive',
      })
    },
  })
}

export const useModifyPaymentPlan = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ planId, data }: { planId: string; data: { installments: number; installmentAmount: number } }) =>
      paymentPlansApi.modifyPaymentPlan(planId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentPlanKeys.lists() })
      queryClient.invalidateQueries({ queryKey: agreementKeys.lists() })
      queryClient.invalidateQueries({ queryKey: agreementKeys.stats() })
      toast({
        title: 'Éxito',
        description: 'Plan de pago modificado correctamente',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al modificar el plan de pago',
        variant: 'destructive',
      })
    },
  })
}
